
export interface Author {
    name: string;
    credentials: string;
    avatar: string;
    avatarAlt: string;
}

export interface GuideSection {
    title: string;
    content: string[]; // Array of paragraphs
    image?: string;
    imageAlt?: string;
}

export interface Guide {
    id: string;
    title: string;
    description: string;
    category: string;
    image: string;
    alt: string;
    readTime: string;
    difficulty: 'Beginner' | 'Intermediate' | 'Advanced';
    rating: number;
    reviewCount: number;
    author: Author;
    publishedDate?: string;
    sections?: GuideSection[];
    tips?: string[];
}


export const featuredGuide: Guide = {
    id: 'featured-1',
    title: 'Complete Nutritional Guide: Science-Based Feeding for Optimal Pet Health',
    description: 'Comprehensive evidence-based nutrition guide covering species-specific dietary requirements, portion control, feeding schedules, and understanding pet food labels. Learn how to select quality foods, prevent obesity, and address special dietary needs for puppies, kittens, seniors, and pets with health conditions.',
    category: 'Nutrition',
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_15dc5bc76-1766814969419.png",
    alt: 'Variety of healthy pet foods including kibble, raw meat, vegetables, and supplements arranged on wooden table',
    readTime: '18 min read',
    author: {
        name: 'Dr. Sarah Mitchell',
        credentials: 'DVM, DACVB, Board Certified Veterinary Nutritionist',
        avatar: '/assets/images/dr-sarah.jpg',
        avatarAlt: 'Dr. Sarah Mitchell'
    },
    rating: 4.9,
    reviewCount: 128,
    difficulty: 'Intermediate',
    publishedDate: 'October 15, 2023',
    tips: [
        "Consult your vet before major diet changes to rule out underlying health issues.",
        "Transition new foods gradually over 7-10 days to avoid upset stomach and diarrhea.",
        "Wash food bowls daily with hot soapy water to prevent bacterial growth.",
        "Store dry food in its original bag inside an airtight container to preserve freshness."
    ],
    sections: [
        {
            title: 'Understanding Species-Specific Needs',
            content: [
                'Just like humans, pets require a balanced diet to thrive. However, the definition of "balanced" varies significantly between species. Dogs are facultative carnivores, meaning they can survive on a plant-based diet but thrive on meat. They have adapted over thousands of years to digest starch, but their primary protein source should still be animal-based.',
                'Cats, on the other hand, are obligate carnivores. This means they strictly require nutrients found in animal tissue, such as taurine, arachidonic acid, and preformed Vitamin A. Their digestive tracts are shorter and acidic, designed to process protein and fat efficiently.',
                'It is crucial to understand these fundamental differences before selecting a diet. Feeding a cat dog food detailedly can lead to severe malnutrition and health issues like blindness (from taurine deficiency) and heart disease (dilated cardiomyopathy).'
            ]
        },
        {
            title: 'Macronutrients: Protein, Fats, and Carbs',
            content: [
                'Protein is the building block of life. For active pets, high-quality animal protein sources are essential for muscle maintenance, tissue repair, and immune function. Look for named meat meals (like "Chicken Meal") which are concentrated sources of protein.',
                'Fats provide the most concentrated source of energy—more than twice that of protein or carbohydrates. They are vital for skin and coat health, brain development, and the absorption of fat-soluble vitamins (A, D, E, K). Omega-3 and Omega-6 fatty acids are particularly important for reducing inflammation.',
                'Carbohydrates, while not strictly essential for dogs and cats in the same way as protein and fats, provide a readily available energy source and fiber for digestive health. Common sources include rice, oats, barley, and potatoes. However, excess carbohydrates, especially in sedentary pets, can lead to obesity and insulin resistance.'
            ]
        },
        {
            title: 'Reading Pet Food Labels',
            content: [
                'Navigating pet food labels can be confusing. Look for specific meat sources (e.g., "Chicken", "Beef", "Salmon") as the first ingredient rather than generic "meat by-products" or "poultry meal". While by-products can be nutritious (organ meats), named sources offer more transparency.',
                'Avoid foods with excessive fillers like corn, wheat, and soy if your pet has known sensitivities or allergies. These ingredients are often used to bulk up the food cheaply.',
                'The AAFCO (Association of American Feed Control Officials) statement is your guarantee that the food is nutritionally complete and balanced. Ensure the food is appropriate for your pet\'s life stage (puppy/kitten, adult, or senior). "All Life Stages" food is formulated to meet the high demands of growth, which may be too calorie-dense for an older, inactive pet.'
            ]
        },
        {
            title: 'Supplements: Do You Need Them?',
            content: [
                'Most commercial pet foods are complete and balanced, meaning supplements are unnecessary for a healthy pet. In fact, over-supplementation can be dangerous. For example, too much Calcium can cause skeletal issues in large breed puppies.',
                'However, specific conditions may benefit from additives. Glucosamine and Chondroitin are excellent for joint health in seniors. Probiotics can aid digestion in sensitive stomachs. Fish oil is a great all-rounder for skin and coat.',
                'Always consult your veterinarian before adding supplements to your pet\'s diet to ensure they don\'t interfere with their regular food or medications.'
            ]
        }
    ]
};

export const allGuides: Guide[] = [
    {
        id: 'guide-1',
        title: 'Essential Nutrition Guide for Adult Dogs',
        description: 'Comprehensive nutritional requirements, feeding schedules, and dietary considerations for maintaining optimal health in adult dogs of all breeds and sizes.',
        category: 'Dogs',
        image: "https://images.unsplash.com/photo-1627244228942-689b73208739",
        alt: 'Healthy adult labrador retriever eating from stainless steel bowl in modern kitchen',
        readTime: '12 min read',
        difficulty: 'Beginner',
        rating: 4.8,
        reviewCount: 234,
        author: {
            name: 'Dr. Michael Chen',
            credentials: 'DVM, PhD',
            avatar: "https://img.rocket.new/generatedImages/rocket_gen_img_1445acc88-1763298867979.png",
            avatarAlt: 'Professional headshot of Asian male veterinarian in white coat with stethoscope'
        },
        tips: [
            "Monitor weight weekly and adjust portions.",
            "Ensure fresh water is always available.",
            "Treats should not exceed 10% of daily calories."
        ],
        sections: [
            {
                title: 'Caloric Needs of Adult Dogs',
                content: [
                    'Adult dogs have different caloric needs depending on their activity level, breed, metabolism, and neuter status. A working border collie will require significantly more calories than a sedentary bulldog, even if they weigh the same.',
                    'Neutered and spayed dogs often have slower metabolisms and may require fewer calories to prevent weight gain. It is important to feed to your dog\'s ideal weight, not their current weight if they are overweight.',
                    'A general rule of thumb is to monitor your dog\'s body condition score (BCS). You should be able to feel their ribs with light pressure but not see them prominently. Adjust food intake by 10% up or down based on this score.'
                ]
            },
            {
                title: 'Hydration Strategies',
                content: [
                    'Water is the most important nutrient. Always ensure your dog has access to fresh, clean water. Dehydration can lead to serious health issues, especially in active dogs or hot climates. Signs of dehydration include dry gums, sunken eyes, and lethargy.',
                    'If your dog is reluctant to drink, try adding ice cubes to their bowl, using a pet fountain, or adding a splash of bone broth to their water to encourage consumption.'
                ]
            },
            {
                title: 'Frequency of Feeding',
                content: [
                    'For most adult dogs, feeding twice a day (morning and evening) is optimal. This helps regulate digestion, keeps blood sugar levels stable, and prevents hunger-induced bile vomiting.',
                    'Small breeds with fast metabolisms may benefit from three meals a day, while some large breeds may do okay with one (though two is still recommended to reduce bloat risk in susceptible breeds).'
                ]
            }
        ]
    },
    {
        id: 'guide-2',
        title: 'Understanding Cat Behavior: Communication & Body Language',
        description: 'Decode your cat\'s mysterious behaviors and learn to recognize signs of stress, contentment, and health issues through body language and vocalizations.',
        category: 'Cats',
        image: "https://images.unsplash.com/photo-1612094449636-879b08221a07",
        alt: 'Tabby cat with alert ears and focused eyes displaying attentive body language on wooden surface',
        readTime: '10 min read',
        difficulty: 'Intermediate',
        rating: 4.9,
        reviewCount: 189,
        author: {
            name: 'Dr. Emily Rodriguez',
            credentials: 'DVM, CABC',
            avatar: "https://img.rocket.new/generatedImages/rocket_gen_img_11395fdb9-1763298869389.png",
            avatarAlt: 'Professional portrait of Hispanic female veterinarian with brown hair in medical scrubs'
        },
        tips: [
            "Never punish a cat for communication.",
            "Observe context; a wagging tail isn't always happy.",
            "Slow blinks are a sign of trust and affection."
        ],
        sections: [
            {
                title: 'The Tail Tells a Tale',
                content: [
                    'A cat\'s tail is a barometer of their mood. A high, upright tail usually signals confidence and friendliness. If the tip is hooked, they are feeling friendly but maybe a bit unsure. A puffed-up tail is a clear sign of fear or aggression, an attempt to look larger to ward off threats.',
                    'A slow swish can mean intense focus (like when hunting) or rising annoyance. A thrashing tail is a warning: "Stop what you are doing!"'
                ]
            },
            {
                title: 'Vocalizations',
                content: [
                    'Cats have a wide range of vocalizations beyond the meow. In fact, adult cats rarely meow at each other; it is a behavior learned primarily to communicate with humans. Short meows are greetings, while drawn-out meows can be complaints or demands.',
                    'Purring generally means contentment but can also be a self-soothing mechanism when a cat is in pain or stressed. Hissing, growling, and yowling are defensive sounds meant to create distance.',
                    'Chirping or chattering is often seen when cats are observing prey they cannot reach, like birds through a window. It mimics the "kill bite" and signals excitement and frustration.'
                ]
            },
            {
                title: 'Ear and Eye Signals',
                content: [
                    'Forward-facing ears indicate interest and alertness. Ears flattened sideways ("airplane ears") suggest annoyance or fear. Ears pinned back flat against the head are a sign of aggression or extreme fear.',
                    'Pupil size can also be telling. Dilated pupils can mean excitement or fear (fight or flight). Constricted pupils might indicate aggression. Exploring the "slow blink" is a great way to bond: if your cat looks at you and slowly closes their eyes, they are saying "I trust you."'
                ]
            }
        ]
    },
    {
        id: 'guide-3',
        title: 'Exotic Bird Care: Housing, Diet & Enrichment',
        description: 'Complete care guide for parrots, cockatiels, and other exotic birds covering proper housing, species-specific nutrition, and mental stimulation activities.',
        category: 'Birds',
        image: "https://images.unsplash.com/photo-1698704141862-6844513f13e3",
        alt: 'Colorful macaw parrot perched on natural wood branch in spacious aviary with toys',
        readTime: '18 min read',
        difficulty: 'Advanced',
        rating: 4.7,
        reviewCount: 156,
        author: {
            name: 'Dr. James Patterson',
            credentials: 'DVM, ABVP',
            avatar: "https://img.rocket.new/generatedImages/rocket_gen_img_1e95cb24a-1763296883553.png",
            avatarAlt: 'Professional photo of Caucasian male veterinarian with gray hair in blue medical uniform'
        },
        tips: [
            "Rotate toys daily to prevent boredom.",
            "Allow at least 2 hours of out-of-cage time.",
            "Avoid Teflon pans; fumes are deadly to birds."
        ],
        sections: [
            {
                title: 'Cage Requirements',
                content: [
                    'Birds need space to spread their wings. The cage should be large enough for them to fly or at least hop around comfortably without their tail feathers touching the sides. For larger parrots, an indoor aviary or bird room is ideal.',
                    'Bar spacing is critical. If bars are too wide, a bird can get their head stuck. If too narrow, they might catch a toe. Stainless steel or powder-coated cages are safest; avoid zinc or lead coatings which are toxic.',
                    'Perch variety is essential for foot health. Use natural wood branches of varying diameters to exercise their feet and prevent pressure sores (bumblefoot). Avoid sandpaper covers.'
                ]
            },
            {
                title: 'Mental Stimulation',
                content: [
                    'Birds, especially parrots, are highly intelligent, often compared to toddlers in their cognitive abilities. They require daily mental stimulation to prevent behavioral issues like screaming or feather plucking.',
                    'Foraging toys are excellent enrichment. Hide treats in paper, cardboard boxes, or specialized puzzle toys to make them "work" for their food. This mimics natural behaviors.',
                    'Social interaction is also vital. Most birds are flock animals and view you as their flock. They need dedicated time outside the cage interacting with their human family every day.'
                ]
            }
        ]
    },
    {
        id: 'guide-4',
        title: 'Bearded Dragon Complete Care Manual',
        description: 'Expert guidance on creating optimal habitats, temperature regulation, UVB lighting requirements, and nutritional needs for healthy bearded dragons.',
        category: 'Reptiles',
        image: "https://images.unsplash.com/photo-1679134896668-60eec9eae38e",
        alt: 'Orange bearded dragon basking under heat lamp on natural rock formation in terrarium',
        readTime: '14 min read',
        difficulty: 'Intermediate',
        rating: 4.6,
        reviewCount: 142,
        author: {
            name: 'Dr. Lisa Thompson',
            credentials: 'DVM, Reptile Specialist',
            avatar: "https://img.rocket.new/generatedImages/rocket_gen_img_13e1df70a-1763301872470.png",
            avatarAlt: 'Professional headshot of African American female veterinarian in white lab coat smiling'
        },
        tips: [
            "Use a temp gun to verify basking spots.",
            "Replace UVB bulbs every 6 months even if they still light up.",
            "Bathe weekly to aid hydration and shedding."
        ],
        sections: [
            {
                title: 'Lighting and Heating',
                content: [
                    'Bearded dragons are desert animals and require high temperatures. Create a temperature gradient in their enclosure: a hot basking spot (95-105°F for adults, 105-110°F for babies) and a cool side (75-85°F). Nighttime drops to 70°F are acceptable.',
                    'UVB lighting is non-negotiable. Without it, dragons cannot metabolize calcium, leading to Metabolic Bone Disease (MBD), a painful and fatal condition. Use a tube UVB bulb (T5 or T8) that spans 2/3 the length of the tank, not a coil bulb which provides uneven coverage.',
                    'Replace UVB bulbs every 6-12 months as their output diminishes long before the light burns out.'
                ]
            },
            {
                title: 'Substrate Choices',
                content: [
                    'Choosing the right bedding is controversial. Loose substrates like sand carry an impaction risk, especially for juveniles who might accidentally ingest it while eating.',
                    'Safer alternatives include slate tile, paper towels, reptile carpet, or non-adhesive shelf liner. These are easy to clean and pose no ingestion risk. For adults, a bioactive mix of topsoil and play sand can be appropriate if husbandry is perfect.'
                ]
            }
        ]
    },
    {
        id: 'guide-5',
        title: 'Rabbit Care Essentials: Housing, Diet & Health',
        description: 'Comprehensive guide to providing optimal care for domestic rabbits including proper housing, hay-based diets, and common health concerns.',
        category: 'Small Pets',
        image: "https://img.rocket.new/generatedImages/rocket_gen_img_16773d5c9-1767636303316.png",
        alt: 'White and brown lop-eared rabbit eating fresh hay in clean indoor enclosure',
        readTime: '11 min read',
        difficulty: 'Beginner',
        rating: 4.8,
        reviewCount: 201,
        author: {
            name: 'Dr. Robert Williams',
            credentials: 'DVM, Exotic Pet Specialist',
            avatar: "https://img.rocket.new/generatedImages/rocket_gen_img_17e1c8542-1763293676786.png",
            avatarAlt: 'Professional portrait of Caucasian male veterinarian with beard in green scrubs'
        },
        tips: [
            "Unlimited hay is non-negotiable.",
            "Check teeth regularly for overgrowth.",
            "Spay/neuter to prevent reproductive cancers."
        ],
        sections: [
            {
                title: 'Dietary Staples',
                content: [
                    'Hay should make up the vast majority (80%+) of a rabbit\'s diet. Timothy hay is excellent for adults. It aids digestion and, crucially, helps wear down their continuously growing teeth. Alfalfa hay is too rich in calcium for adults and should be reserved for young buns.',
                    'Fresh vegetables like romaine lettuce, herbs (cilantro, parsley), and bok choy should be provided daily (about 1 cup per 4lbs of body weight). Avoid iceberg lettuce which has low nutritional value.',
                    'Pellets should be high-quality and timothy-based, limited to 1/4 cup per day for adults to prevent obesity.'
                ]
            },
            {
                title: 'Free Roaming vs. Hutch',
                content: [
                    'The traditional outdoor hutch is outdated. Rabbits thrive indoors as part of the family. "Free-roaming" rabbits are litter trained and have run of the house, similar to cats.',
                    'If containment is needed, an exercise pen (x-pen) offers much more space than a cage. Rabbits need room to hop, stretch, and perform "binkies" (joyful jumps). Flooring should be soft to prevent sore hocks.'
                ]
            }
        ]
    },
    {
        id: 'guide-6',
        title: 'Senior Dog Care: Health, Nutrition & Comfort',
        description: 'Specialized care strategies for aging dogs covering joint health, dietary adjustments, cognitive support, and quality of life enhancements.',
        category: 'Dogs',
        image: "https://images.unsplash.com/photo-1663227759465-39b1c05b4355",
        alt: 'Gray-muzzled senior golden retriever resting comfortably on soft orthopedic dog bed',
        readTime: '13 min read',
        difficulty: 'Intermediate',
        rating: 4.9,
        reviewCount: 267,
        author: {
            name: 'Dr. Sarah Mitchell',
            credentials: 'DVM, DACVB',
            avatar: "https://img.rocket.new/generatedImages/rocket_gen_img_189601afd-1763296832715.png",
            avatarAlt: 'Professional photo of Caucasian female veterinarian with blonde hair in white coat'
        },
        tips: [
            "Consider elevated food bowls for comfort.",
            "Increase frequency of vet visits to twice a year.",
            "Use nightlights for dogs with failing vision."
        ],
        sections: [
            {
                title: 'Joint Health',
                content: [
                    'As dogs age, they often develop arthritis and stiffness. Supplements like glucosamine, chondroitin, and MSM can help maintain joint health. Omega-3 fatty acids from fish oil are excellent natural anti-inflammatories.',
                    'Home modifications are essential. Provide ramps for getting into cars or onto furniture. Cover slick hardwood floors with yoga mats or rugs to provide traction and prevent slips.',
                    'Gentle exercise is still important to maintain muscle mass, but avoid high-impact activities like jumping or long runs. Swimming is a fantastic low-impact option.'
                ]
            },
            {
                title: 'Cognitive Dysfunction',
                content: [
                    'Canine Cognitive Dysfunction (CCD), similar to dementia in humans, affects many seniors. Symptoms include pacing, staring at walls, getting stuck in corners, changes in sleep-wake cycles, and potty accidents.',
                    'Environmental enrichment, puzzle toys, and supplements rich in antioxidants and MCT oil can help support brain health. Maintaining a predictable routine helps reduce anxiety in confused seniors.'
                ]
            }
        ]
    },

    // NEW COMPREHENSIVE FOOD & NUTRITION GUIDES
    {
        id: 'guide-food-1',
        title: 'Dog Nutrition Fundamentals: Complete Feeding Guide',
        description: 'Essential macronutrients (proteins, fats, carbohydrates), micronutrients (vitamins, minerals), feeding schedules by life stage, portion control based on weight and activity level, and recognizing quality dog food ingredients.',
        category: 'Dogs',
        image: "https://img.rocket.new/generatedImages/rocket_gen_img_1a9a9eebe-1767637973675.png",
        alt: 'High-quality dog food kibble in stainless steel bowl with fresh vegetables beside it',
        readTime: '16 min read',
        difficulty: 'Beginner',
        rating: 4.9,
        reviewCount: 412,
        author: {
            name: 'Dr. Michael Chen',
            credentials: 'DVM, PhD, Veterinary Nutritionist',
            avatar: "https://img.rocket.new/generatedImages/rocket_gen_img_1445acc88-1763298867979.png",
            avatarAlt: 'Professional headshot of Asian male veterinarian in white coat with stethoscope'
        },
        tips: [
            "Measure food with a scale, not a cup, for accuracy.",
            "Account for treats in daily calorie counts (max 10% of total).",
            "Wash bowls after every meal."
        ],
        sections: [
            {
                title: 'Key Nutrients',
                content: [
                    'Dogs need a balanced mix of proteins, fats, carbohydrates, vitamins, and minerals. Protein supports muscle growth and tissue repair. Fats provide energy, insulation, and skin health.',
                    'Vitamins and minerals are crucial for various bodily functions. Vitamin A supports vision, Vitamin D is vital for bone health, and B vitamins aid metabolism. Minerals like Calcium and Phosphorus must be balanced carefully, especially in growing puppies.'
                ]
            },
            {
                title: 'Life Stage Feeding',
                content: [
                    'Puppies need roughly twice the calories per pound of body weight compared to adults to fuel rapid growth. Large breed puppies need specialized formulas to control growth rates and prevent joint issues.',
                    'Adult maintenance diets are for dogs who have finished growing. Senior diets often have reduced calories and increased fiber to combat slower metabolisms, along with joint support supplements.',
                    'Pregnant and nursing mothers have massive energy demands and should often be fed a high-calorie puppy formula during lactation.'
                ]
            }
        ]
    },
    {
        id: 'guide-food-2',
        title: 'Raw Diet vs. Kibble vs. Wet Food: Complete Comparison for Dogs',
        description: 'Science-based analysis of different feeding approaches including raw diets (BARF), commercial kibble, wet food, and home-cooked meals. Covers nutritional completeness, safety considerations, cost analysis, and transitioning between diets.',
        category: 'Dogs',
        image: "https://images.unsplash.com/photo-1676195467298-2dc1915ef56f",
        alt: 'Comparison display of raw meat, dry kibble, and wet dog food in separate bowls',
        readTime: '14 min read',
        difficulty: 'Intermediate',
        rating: 4.8,
        reviewCount: 356,
        author: {
            name: 'Dr. Lisa Thompson',
            credentials: 'DVM, Canine Nutrition Specialist',
            avatar: "https://img.rocket.new/generatedImages/rocket_gen_img_13e1df70a-1763301872470.png",
            avatarAlt: 'Professional headshot of African American female veterinarian in white lab coat smiling'
        },
        tips: [
            "Consult a vet nutritionist for homemade diets.",
            "Warm wet food slightly to increase palatability.",
            "Freeze raw bones to kill some parasites before feeding."
        ],
        sections: [
            {
                title: 'Dry Food (Kibble)',
                content: [
                    'Pros: Convenient, cost-effective, easy to store, and the crunch can help mechanically clean teeth (though not a substitute for brushing).',
                    'Cons: High processing heat can degrade some nutrients (which are added back). Often higher in carbohydrates. Low moisture content means dogs must drink more water.'
                ]

            },
            {
                title: 'Wet Food (Canned)',
                content: [
                    'Pros: High moisture content is great for hydration and kidney health. Generally more palatable for picky eaters. Often higher in protein and lower in carbs than kibble.',
                    'Cons: More expensive per calorie. specific once opened, it must be refrigerated and used quickly. Can contribute to tartar buildup if dental care isn\'t maintained.'
                ]
            },
            {
                title: 'Raw Diets (BARF/PMR)',
                content: [
                    'Pros: Mimics ancestral diet, highly digestible, often results in smaller stools and healthier coats.',
                    'Cons: Risk of bacterial contamination (Salmonella, E. coli) for both dog and human. It can be difficult to balance nutritionally without expert guidance. Bone fragments can pose choking or blockage hazards.'
                ]
            }
        ]
    },
    {
        id: 'guide-food-3',
        title: 'Cat Obligate Carnivore Nutrition: Species-Specific Requirements',
        description: 'Understanding feline dietary needs including essential amino acids (taurine, arginine), appropriate protein-to-fat ratios, hydration strategies, preventing urinary issues, and the dangers of vegan/vegetarian diets for cats.',
        category: 'Cats',
        image: "https://images.unsplash.com/photo-1633761971690-ee5f3aca8415",
        alt: 'White and gray cat eating premium wet food from ceramic bowl',
        readTime: '13 min read',
        difficulty: 'Intermediate',
        rating: 4.9,
        reviewCount: 298,
        author: {
            name: 'Dr. Emily Rodriguez',
            credentials: 'DVM, CABC, Feline Nutrition Expert',
            avatar: "https://img.rocket.new/generatedImages/rocket_gen_img_11395fdb9-1763298869389.png",
            avatarAlt: 'Professional portrait of Hispanic female veterinarian with brown hair in medical scrubs'
        },
        tips: [
            "Cats prefer running water; consider a fountain.",
            "Fish should be an occasional treat, not a staple (high mercury/magnesium).",
            "Never starve a fat cat; rapid weight loss causes liver failure."
        ],
        sections: [
            {
                title: 'Why Meat is Non-Negotiable',
                content: [
                    'Cats cannot synthesize certain essential nutrients like taurine, arginine, and arachidonic acid. They MUST obtain them from animal tissues. Deficiency can be fatal—taurine deficiency leads to blindness and heart failure.',
                    'A vegan or vegetarian diet is fundamentally incompatible with feline physiology and is considered animal cruelty by many veterinary bodies.'
                ]
            },
            {
                title: 'The Thirst Factor',
                content: [
                    'Ancestral cats were desert dwellers who got most of their water from prey. Modern domestic cats still have a low thirst drive. Feeding only dry food can lead to chronic low-level dehydration, contributing to urinary tract infections (UTIs), crystals, and kidney disease.',
                    'Feeding at least some wet food daily is the easiest way to support your cat\'s urinary health.'
                ]
            }
        ]
    },
    {
        id: 'guide-food-4',
        title: 'Exotic Bird Diet: Species-Specific Nutritional Needs',
        description: 'Comprehensive feeding guide for parrots, cockatiels, finches, and canaries. Covers seed mixes, pellets, fresh vegetables, fruits, toxic foods to avoid, calcium supplements, and preventing obesity in captive birds.',
        category: 'Birds',
        image: "https://images.unsplash.com/photo-1558246597-828fbde7240e",
        alt: 'Colorful parrot eating fresh fruits and vegetables from feeding bowl',
        readTime: '15 min read',
        difficulty: 'Advanced',
        rating: 4.7,
        reviewCount: 187,
        author: {
            name: 'Dr. James Patterson',
            credentials: 'DVM, ABVP, Avian Medicine Specialist',
            avatar: "https://img.rocket.new/generatedImages/rocket_gen_img_1e95cb24a-1763296883553.png",
            avatarAlt: 'Professional photo of Caucasian male veterinarian with gray hair in blue medical uniform'
        },
        tips: [
            "Wash fruits thoroughly to remove pesticides.",
            "Sprouted seeds are healthier than dry seeds (lower fat, higher vit A).",
            "Remove fresh food after 4 hours to prevent spoilage."
        ],
        sections: [
            {
                title: 'The Seed Diet Myth',
                content: [
                    'For decades, it was common to feed birds only seeds. We now know this is malnutrition. Seeds are high in fat and low in Vitamin A and Calcium. An all-seed diet leads to obesity, fatty liver disease, and poor feather quality.',
                    'A balanced diet for most parrots is roughly 60-70% high-quality formulated pellets, 20-30% fresh vegetables/grains/fruits, and only 10% seeds/nuts as treats.'
                ]
            },
            {
                title: 'The "Chop" Concept',
                content: [
                    'Bird owners often prepare "chop"—a finely chopped mix of raw vegetables, leafy greens, cooked grains (quinoa, brown rice), and legumes. This can be made in bulk and frozen.',
                    'Favorites include broccoli, carrots, peppers (birds can\'t taste heat!), kale, and sweet potatoes (cooked). Avoid avocado, chocolate, caffeine, and fruit pits/seeds which are toxic.'
                ]
            }
        ]
    },
    {
        id: 'guide-food-5',
        title: 'Reptile Nutrition: Herbivore, Carnivore & Omnivore Feeding',
        description: 'Species-appropriate diets for bearded dragons, ball pythons, leopard geckos, and turtles. Covers live prey feeding, gut-loading insects, calcium/D3 supplementation, vegetable variety, and feeding schedules.',
        category: 'Reptiles',
        image: "https://images.unsplash.com/photo-1710969835260-54ec82cd05b5",
        alt: 'Bearded dragon eating mixture of insects and fresh greens on feeding platform',
        readTime: '17 min read',
        difficulty: 'Advanced',
        rating: 4.8,
        reviewCount: 203,
        author: {
            name: 'Dr. Lisa Thompson',
            credentials: 'DVM, Reptile & Amphibian Specialist',
            avatar: "https://img.rocket.new/generatedImages/rocket_gen_img_13e1df70a-1763301872470.png",
            avatarAlt: 'Professional headshot of African American female veterinarian in white lab coat smiling'
        },
        tips: [
            "Variety is key to preventing nutritional deficiencies.",
            "Remove uneaten live prey after 15 minutes prevents biting injuries.",
            "Calcium powder needs D3 for indoor reptiles."
        ],
        sections: [
            {
                title: 'Gut Loading Insects',
                content: [
                    'Feeder insects like crickets and mealworms are nutritionally empty on their own. You must "gut load" them by feeding them high-calcium, nutrient-dense vegetables (collard greens, sweet potato, squash) for 24-48 hours before feeding them to your reptile.',
                    'You are essentially feeding your reptile whatever is inside the insect\'s stomach.'
                ]
            },
            {
                title: 'Supplementation Schedule',
                content: [
                    'Most insectivorous and herbivorous reptiles need Calcium supplementation at nearly every feeding. Calcium with Vitamin D3 is usually needed for indoor animals, while plain Calcium is used for those with outdoor sun access.',
                    'Multivitamins are typically given once a week. Over-supplementation of Vitamin D3 can be toxic, so follow vet guidelines carefully.'
                ]
            }
        ]
    },
    {
        id: 'guide-food-6',
        title: 'Small Mammal Nutrition: Rabbits, Guinea Pigs & Hamsters',
        description: 'Hay-based diets for herbivores, pellet selection guidelines, fresh vegetable introduction, treating safely, vitamin C requirements for guinea pigs, and preventing dental disease through proper nutrition.',
        category: 'Small Pets',
        image: "https://images.unsplash.com/photo-1681773769926-058469f5234f",
        alt: 'Brown rabbit eating fresh timothy hay with pellets and vegetables nearby',
        readTime: '12 min read',
        difficulty: 'Beginner',
        rating: 4.8,
        reviewCount: 267,
        author: {
            name: 'Dr. Robert Williams',
            credentials: 'DVM, Small Mammal Specialist',
            avatar: "https://img.rocket.new/generatedImages/rocket_gen_img_17e1c8542-1763293676786.png",
            avatarAlt: 'Professional portrait of Caucasian male veterinarian with beard in green scrubs'
        },
        tips: [
            "Store pellets in a cool dry place (vitamin C degrades quickly).",
            "Avoid yogurt drops; they are too high in sugar.",
            "Introduce new veggies one at a time to monitor poop."
        ],
        sections: [
            {
                title: 'Vitamin C for Guinea Pigs',
                content: [
                    'Like humans, guinea pigs cannot make their own Vitamin C. They need it in their diet daily (10-30mg/kg) to prevent scurvy, which causes joint pain, dental issues, and rough coat.',
                    'Bell peppers (especially red ones) are an excellent daily source. Vitamin C deficient pellets are common if the bag is old, as the vitamin degrades in light and air. Supplement tabs are safer than water additives which degrade quickly.'
                ]
            },
            {
                title: 'The Danger of Muesli',
                content: [
                    'Avoid "muesli" style mixes with colorful seeds and flakes for rabbits and guinea pigs. Pets will "selective feed," picking out the sugary bits and leaving the healthy fiber pellets. This leads to unbalanced nutrition and dental disease.',
                    'Stick to uniform, plain Timothy hay-based pellets.'
                ]
            }
        ]
    },

    // NEW COMPREHENSIVE CARE & GUIDANCE GUIDES
    {
        id: 'guide-care-1',
        title: 'Puppy Development Stages: Week-by-Week Growth Guide',
        description: 'Comprehensive timeline of puppy development from birth to 1 year covering socialization windows, vaccination schedules, behavioral milestones, teething management, house training progression, and age-appropriate activities.',
        category: 'Dogs',
        image: "https://images.unsplash.com/photo-1733760093533-fd1a4e43be89",
        alt: 'Eight-week-old golden retriever puppy playing with toy in living room',
        readTime: '19 min read',
        difficulty: 'Intermediate',
        rating: 4.9,
        reviewCount: 445,
        author: {
            name: 'Dr. Sarah Mitchell',
            credentials: 'DVM, DACVB, Puppy Behavior Specialist',
            avatar: "https://img.rocket.new/generatedImages/rocket_gen_img_189601afd-1763296832715.png",
            avatarAlt: 'Professional photo of Caucasian female veterinarian with blonde hair in white coat'
        },
        tips: [
            "Enroll in puppy classes early (after first round of shots).",
            "Take lots of photos; they grow fast!",
            "Hand-feed meals to assist with bite inhibition training."
        ],
        sections: [
            {
                title: 'Critical Socialization Period (8-16 Weeks)',
                content: [
                    'This is the most important time in your dog\'s life. Positive exposures now shape their entire worldview. The goal isn\'t just "meeting dogs," but seeing people of all sizes/colors, hearing city noises, walking on different surfaces, and handling body parts.',
                    'The Rule of 7s: By 12 weeks, try to have your puppy experience 7 new surfaces, 7 new people, 7 new toys, etc. Keep every interaction happy and treat-filled. If they look scared, retreat and make it easier.'
                ]
            },
            {
                title: 'The Teething Phase (3-6 Months)',
                content: [
                    'Shark mode activated! As baby teeth fall out and adult teeth erupt, puppies need to chew to relieve pain. Provide a variety of textures: frozen carrots, rubber Kongs, and soft looped ropes.',
                    'Protect your furniture and hands by redirecting biting to appropriate toys immediately. Never punish growling; it\'s a warning signal you want to keep.'
                ]
            }
        ]
    },
    {
        id: 'guide-care-2',
        title: 'Senior Dog Care: Managing Aging & Quality of Life',
        description: 'Complete guide to caring for aging dogs covering arthritis management, cognitive decline signs, dietary adjustments, exercise modifications, pain recognition, when to consider quality-of-life evaluations, and end-of-life planning.',
        category: 'Dogs',
        image: "https://images.unsplash.com/photo-1663227759465-39b1c05b4355",
        alt: 'Gray-muzzled senior golden retriever resting comfortably on orthopedic dog bed',
        readTime: '16 min read',
        difficulty: 'Advanced',
        rating: 4.9,
        reviewCount: 389,
        author: {
            name: 'Dr. Michael Chen',
            credentials: 'DVM, PhD, Geriatric Veterinary Medicine',
            avatar: "https://img.rocket.new/generatedImages/rocket_gen_img_1445acc88-1763298867979.png",
            avatarAlt: 'Professional headshot of Asian male veterinarian in white coat with stethoscope'
        },
        tips: [
            "Keep a journal of your dog's good and bad days.",
            "Massages can help soothe achy muscles.",
            "Use a distinct perfume/scent when you enter the room if vision fails."
        ],
        sections: [
            {
                title: 'Comfort Measures',
                content: [
                    'Simple environmental changes can drastically improve a senior\'s confidence. Ramps or stairs for furniture prevent jarring joint impacts. Raised food bowls reduce neck strain.',
                    'Yoga mats or carpet runners on slick floors provide necessary traction for weak hind legs. Orthopedic memory foam beds protect pressure points from calluses and sores.'
                ]
            },
            {
                title: 'Assessing Quality of Life',
                content: [
                    'This is the hardest part of pet ownership. Tools like the "HHHHHMM Scale" (Hurt, Hunger, Hydration, Hygiene, Happiness, Mobility, More Good Days than Bad) provide an objective way to track decline.',
                    'Consult your vet early about palliative care and pain management. Modern medicine can keep seniors comfortable for a long time.'
                ]
            }
        ]
    },
    {
        id: 'guide-care-3',
        title: 'Cat Stress Management: Creating a Calm Environment',
        description: 'Understanding feline stress triggers, environmental enrichment strategies, vertical space importance, litter box optimization (rule of N+1), pheromone diffusers, multi-cat household management, and recognizing stress-related health issues.',
        category: 'Cats',
        image: "https://images.unsplash.com/photo-1596566437341-23c074f26b6f",
        alt: 'Relaxed tabby cat lounging in elevated perch near window with toys nearby',
        readTime: '14 min read',
        difficulty: 'Intermediate',
        rating: 4.8,
        reviewCount: 334,
        author: {
            name: 'Dr. Emily Rodriguez',
            credentials: 'DVM, CABC, Feline Behavioral Medicine',
            avatar: "https://img.rocket.new/generatedImages/rocket_gen_img_11395fdb9-1763298869389.png",
            avatarAlt: 'Professional portrait of Hispanic female veterinarian with brown hair in medical scrubs'
        },
        tips: [
            "Use Feliway diffusers to reduce tension.",
            "Play with your cat to burn off nervous energy.",
            "Ensure every resource has an 'exit route' (no cornering)."
        ],
        sections: [
            {
                title: 'The Superhighway: Vertical Space',
                content: [
                    'Cats live in three dimensions. "Catification" involves creating routes for cats to traverse a room without touching the floor. This increases their territory size and confidence.',
                    'Tall cat trees, window perches, and wall shelves give cats a vantage point to survey their domain. This is crucial in multi-cat homes so dominant cats don\'t block floor paths.'
                ]
            },
            {
                title: 'The Litter Box Rules',
                content: [
                    'Litter box stress is a top cause of house soiling. Follow the "1 + 1" rule: one box per cat, plus one extra. Place them in different locations, not all lined up.',
                    'Most cats prefer large, uncovered boxes with unscented clumping litter. Clean them daily. A dirty box is like a dirty public restroom—your cat will avoid it.'
                ]
            }
        ]
    },
    {
        id: 'guide-care-4',
        title: 'Parrot Enrichment: Mental Stimulation & Behavioral Health',
        description: 'Preventing boredom, feather plucking, and screaming through proper enrichment. Covers foraging toys, training games, social interaction needs, rotation schedules, DIY enrichment ideas, and recognizing signs of mental distress.',
        category: 'Birds',
        image: "https://img.rocket.new/generatedImages/rocket_gen_img_181ded849-1767634235762.png",
        alt: 'African grey parrot playing with puzzle toy in enriched cage environment',
        readTime: '15 min read',
        difficulty: 'Advanced',
        rating: 4.7,
        reviewCount: 214,
        author: {
            name: 'Dr. James Patterson',
            credentials: 'DVM, ABVP, Avian Behavior Specialist',
            avatar: "https://img.rocket.new/generatedImages/rocket_gen_img_1e95cb24a-1763296883553.png",
            avatarAlt: 'Professional photo of Caucasian male veterinarian with gray hair in blue medical uniform'
        },
        tips: [
            "Avoid mirrors; they can cause fixation and hormonal issues.",
            "Teach foraging early to build independence.",
            "Target training allows for stress-free vet handling."
        ],
        sections: [
            {
                title: 'Foraging: The Day Job',
                content: [
                    'In the wild, parrots spend most of their day searching for food. In captivity, a bowl of seeds is "boring" and leaves them with hours of pent-up energy. Mimic nature by using foraging toys.',
                    'Start simple: cover a bowl with paper they have to punch through. Graduate to hiding treats in complex puzzle toys, woven mats, or cardboard boxes. A busy beak is a quiet beak.'
                ]
            },
            {
                title: 'Destruction is Good',
                content: [
                    'Parrots need to chew. Provide appropriate destructible toys made of soft wood like balsa, pine, paper, and yucca. Destroying these toys satisfies their natural urge to verify nesting cavities and keeps beaks trimmed.',
                    'Don\'t get mad when they chew your furniture if you haven\'t provided a better alternative!'
                ]
            }
        ]
    },
    {
        id: 'guide-care-5',
        title: 'Reptile Habitat Setup: Temperature, Lighting & Humidity Control',
        description: 'Species-specific environmental requirements including basking temperatures, UVB lighting schedules, humidity zones, substrate selection, hiding spots, and monitoring equipment. Covers common setup mistakes and troubleshooting.',
        category: 'Reptiles',
        image: "https://img.rocket.new/generatedImages/rocket_gen_img_12a25171e-1767634233428.png",
        alt: 'Well-designed terrarium with temperature gradient zones, UVB lighting, and naturalistic decor',
        readTime: '18 min read',
        difficulty: 'Advanced',
        rating: 4.8,
        reviewCount: 256,
        author: {
            name: 'Dr. Lisa Thompson',
            credentials: 'DVM, Reptile Husbandry Expert',
            avatar: "https://img.rocket.new/generatedImages/rocket_gen_img_13e1df70a-1763301872470.png",
            avatarAlt: 'Professional headshot of African American female veterinarian in white lab coat smiling'
        },
        tips: [
            "Use a thermostat for ALL heat sources to prevent burns.",
            "Setup the tank at least 48 hours before bringing the animal home.",
            "Research the adult size of the reptile before buying a tank."
        ],
        sections: [
            {
                title: 'Thermoregulation',
                content: [
                    'Reptiles utilize their environment to control body temperature. You must provide a "gradient"—a hot side and a cool side. This allows them to move back and forth to regulate metabolism.',
                    'Heat rocks are dangerous and should be avoided (common cause of thermal burns). Use overhead ceramic heat emitters or basking bulbs, or under-tank heat mats regulated by a thermostat.'
                ]
            },
            {
                title: 'Humidity and Hydration',
                content: [
                    'Humidity requirements vary wildly. A bearded dragon needs dry air (30-40%), while a ball python needs tropical humidity (60-80%). Incorrect humidity leads to stuck shed (dysecdysis) or respiratory infections.',
                    'Use digital hygrometers. To increase humidity, mist the tank, use a larger water bowl, or add a "humid hide" with damp moss.'
                ]
            }
        ]
    },
    {
        id: 'guide-care-6',
        title: 'Guinea Pig Social Needs: Companionship & Group Dynamics',
        description: 'Understanding guinea pig herd behavior, proper pairing/grouping, introducing new pigs, cage size requirements for groups, recognizing dominance behaviors, and managing social conflicts. Why guinea pigs should never live alone.',
        category: 'Small Pets',
        image: "https://images.unsplash.com/photo-1696229762279-cbee12b6126e",
        alt: 'Two guinea pigs cuddling together in spacious enclosure with hiding spots',
        readTime: '11 min read',
        difficulty: 'Intermediate',
        rating: 4.8,
        reviewCount: 302,
        author: {
            name: 'Dr. Robert Williams',
            credentials: 'DVM, Small Mammal Behavior Specialist',
            avatar: "https://img.rocket.new/generatedImages/rocket_gen_img_17e1c8542-1763293676786.png",
            avatarAlt: 'Professional portrait of Caucasian male veterinarian with beard in green scrubs'
        },
        tips: [
            "Boars (males) can live together if introduced young.",
            "Provide two of everything (houses, bowls) to prevent squabbles.",
            "Neutering males allows for safe male-female pairings."
        ],
        sections: [
            {
                title: 'The Herd Mentality',
                content: [
                    'Guinea pigs are prey animals that find safety in numbers. Isolation causes chronic stress. In Switzerland, it is actually illegal to own just one guinea pig.',
                    'Pairs are good, but small herds (one neutered male with several females) are often the most stable dynamic. Watching them interact, chatter, and sleep together is incredibly rewarding.'
                ]
            },
            {
                title: 'Dominance Displays',
                content: [
                    'When introducing pigs, expect some drama. Rumblestrutting (waggling bottom while purring), mounting, and nose-offs are normal. Do not separate unless there is blood or a "flying ball of fur" fight.',
                    'Neutral territory is key for introductions. Use a bathtub or a playpen area where neither pig has claimed ownership.'
                ]
            }
        ]
    },

    // Additional comprehensive guides
    {
        id: 'guide-care-7',
        title: 'Dental Care for Pets: Prevention & Home Maintenance',
        description: 'Species-specific dental health covering brushing techniques, dental-specific diets, chew toys effectiveness, recognizing dental disease signs, anesthesia-free cleanings controversy, and when professional cleaning is necessary.',
        category: 'Dogs',
        image: "https://img.rocket.new/generatedImages/rocket_gen_img_118890e71-1767486237023.png",
        alt: 'Veterinarian examining dog teeth with pet owner holding dog gently',
        readTime: '13 min read',
        difficulty: 'Beginner',
        rating: 4.7,
        reviewCount: 278,
        author: {
            name: 'Dr. Sarah Mitchell',
            credentials: 'DVM, Veterinary Dentistry',
            avatar: "https://img.rocket.new/generatedImages/rocket_gen_img_189601afd-1763296832715.png",
            avatarAlt: 'Professional photo of Caucasian female veterinarian with blonde hair in white coat'
        },
        tips: [
            "Inspect mouth monthly for bad breath or red gums.",
            "Dental chews are a supplement, not a substitute for brushing.",
            "Start handling the muzzle early with puppies."
        ],
        sections: [
            {
                title: 'The Silent Killer',
                content: [
                    'Dental disease isn\'t just about bad breath. Bacteria from infected gums can enter the bloodstream and damage the heart, liver, and kidneys. By age 3, 80% of dogs have some form of periodontal disease.',
                    'The only way to reverse tartar (hardened plaque) is a professional cleaning under anesthesia. Brushing only prevents new plaque from forming.'
                ]
            },
            {
                title: 'Brushing Guide',
                content: [
                    'Daily brushing is the gold standard. Use an enzymatic pet toothpaste (human paste is toxic). Focus on the outside surfaces of the teeth where the cheek touches.',
                    'Start slow: let them lick the paste, then use your finger, then introduce the brush. Make it a positive routine with a treat afterwards.'
                ]
            }
        ]
    },
    {
        id: 'guide-care-8',
        title: 'Grooming Essentials: Coat Care, Nail Trimming & Hygiene',
        description: 'Comprehensive grooming guide covering breed-specific coat maintenance, proper brushing techniques, nail trimming safety, ear cleaning, dental hygiene, and recognizing when professional grooming is needed.',
        category: 'Dogs',
        image: "https://img.rocket.new/generatedImages/rocket_gen_img_1ac0167af-1765205758878.png",
        alt: 'Professional groomer brushing long-haired dog with various grooming tools displayed',
        readTime: '14 min read',
        difficulty: 'Beginner',
        rating: 4.8,
        reviewCount: 321,
        author: {
            name: 'Dr. Michael Chen',
            credentials: 'DVM, Dermatology Specialist',
            avatar: "https://img.rocket.new/generatedImages/rocket_gen_img_1445acc88-1763298867979.png",
            avatarAlt: 'Professional headshot of Asian male veterinarian in white coat with stethoscope'
        },
        tips: [
            "Touch your dog's paws often to desensitize them.",
            "Brush before bathing to remove mats (water tightens them).",
            "Use styptic powder to stop bleeding if you cut a nail quick."
        ],
        sections: [
            {
                title: 'Coat Types Matter',
                content: [
                    'Double-coated dogs (Huskies, Shepherds) blow their coat seasonally and need an undercoat rake. Never shave them, as the coat regulates their temperature.',
                    'Curly coats (Poodles, Doodles) have hair, not fur, which grows continuously. They require slicker brushes and metal combs to prevent painful matting near the skin, plus professional haircuts every 4-8 weeks.'
                ]
            },
            {
                title: 'The Dreaded Nail Trim',
                content: [
                    'Long nails impact posture and can cause joint pain. Listen for clicking on the floor—that\'s too long. Trim small amounts frequently (weekly) to encourage the "quick" (blood vessel) to recede.',
                    'If your dog is terrified, consider a scratchpad where they can file their own nails, or work on desensitization training (Counter-Conditioning).'
                ]
            }
        ]
    },
    {
        id: 'guide-care-9',
        title: 'Multi-Cat Household Harmony: Managing Feline Relationships',
        description: 'Strategies for introducing new cats, preventing territorial aggression, resource distribution (food, litter, perches), recognizing bullying behavior, using vertical space, and when separation is necessary.',
        category: 'Cats',
        image: "https://images.unsplash.com/photo-1632239484316-e0f0a6fba583",
        alt: 'Three cats peacefully coexisting in well-designed multi-level cat tree environment',
        readTime: '15 min read',
        difficulty: 'Advanced',
        rating: 4.9,
        reviewCount: 289,
        author: {
            name: 'Dr. Emily Rodriguez',
            credentials: 'DVM, CABC, Multi-Cat Behavior Expert',
            avatar: "https://img.rocket.new/generatedImages/rocket_gen_img_11395fdb9-1763298869389.png",
            avatarAlt: 'Professional portrait of Hispanic female veterinarian with brown hair in medical scrubs'
        },
        tips: [
            "Introduce scents before visual contact (site swapping).",
            "Go at the pace of the most hesitant cat.",
            "Feed separately to prevent anxiety."
        ],
        sections: [
            {
                title: 'Introductions: The Slow Method',
                content: [
                    'Throwing two cats together and hoping "they work it out" usually disastrous. Complete separation for the first few days is vital. Feed them on opposite sides of a closed door so they associate the other\'s scent with good things (food).',
                    'Visual contact comes later, using a baby gate or screen. Only allow physical contact when both cats are calm and relaxed. This process can take weeks.'
                ]
            },
            {
                title: 'Resource Guarding',
                content: [
                    'Cats don\'t always fight with claws; often they fight with "blocking." A bully cat might sit in a hallway, blocking access to the litter box or food.',
                    'Ensure resources are distributed throughout the house, not clustered in one room. This enables a subordinate cat to access needs without crossing the dominant cat\'s path.'
                ]
            }
        ]
    },
    {
        id: 'guide-care-10',
        title: 'Avian Health Monitoring: Early Disease Detection',
        description: 'Recognizing subtle signs of illness in birds (who hide symptoms), weight monitoring importance, normal vs. abnormal droppings, feather quality assessment, breathing patterns, and establishing baseline health behaviors.',
        category: 'Birds',
        image: "https://img.rocket.new/generatedImages/rocket_gen_img_1c2ab8e1e-1767634233976.png",
        alt: 'Veterinarian performing wellness exam on blue and gold macaw',
        readTime: '13 min read',
        difficulty: 'Intermediate',
        rating: 4.8,
        reviewCount: 198,
        author: {
            name: 'Dr. James Patterson',
            credentials: 'DVM, ABVP, Avian Medicine',
            avatar: "https://img.rocket.new/generatedImages/rocket_gen_img_1e95cb24a-1763296883553.png",
            avatarAlt: 'Professional photo of Caucasian male veterinarian with gray hair in blue medical uniform'
        },
        tips: [
            "Established a relationship with an avian vet before you need one.",
            "Quarantine new birds for 30 days.",
            "Weigh your bird weekly at the same time."
        ],
        sections: [
            {
                title: 'Masters of Disguise',
                content: [
                    'In the wild, a sick bird attracts predators. Thus, pet birds are wired to hide illness until they are critically unwell. "Looking sick" (fluffed up, sleeping on the floor, tail bobbing) is essentially an emergency situation.',
                    'The best tool you have is a gram scale. Weigh your bird weekly. A drop of 10% body weight is a red flag that warrants a vet visit, even if the bird acts normal.'
                ]
            },
            {
                title: 'The Droppings Decoding',
                content: [
                    'Bird poop has three parts: feces (solid, usually green/brown), urates (pasty white), and urine (clear liquid). Learn what is normal for your bird.',
                    'Yellow or lime green urates can signal liver disease. Bubbles in the feces can mean infection. Red (unless they ate berries) is an emergency.'
                ]
            }
        ]
    },
    {
        id: 'guide-care-11',
        title: 'Exotic Pet Travel: Safe Transportation Guidelines',
        description: 'Species-specific travel considerations for reptiles, birds, and small mammals. Covers temperature control, carrier requirements, stress reduction, legal documentation, finding exotic-friendly accommodations, and vet care while traveling.',
        category: 'Reptiles',
        image: "https://img.rocket.new/generatedImages/rocket_gen_img_147a00cf6-1767634235740.png",
        alt: 'Secure travel carrier with proper ventilation containing small reptile with heat pack',
        readTime: '12 min read',
        difficulty: 'Intermediate',
        rating: 4.7,
        reviewCount: 176,
        author: {
            name: 'Dr. Lisa Thompson',
            credentials: 'DVM, Exotic Animal Medicine',
            avatar: "https://img.rocket.new/generatedImages/rocket_gen_img_13e1df70a-1763301872470.png",
            avatarAlt: 'Professional headshot of African American female veterinarian in white lab coat smiling'
        },
        tips: [
            "Do not feed reptiles 24 hours before travel to prevent regurgitation.",
            "Cover carrier to reduce visual stimuli.",
            "Bring a copy of vet records and permits."
        ],
        sections: [
            {
                title: 'Heat Support on the Go',
                content: [
                    'Reptiles need to stay warm. In winter, pre-warm the car. Use shipping heat packs (UniHeat) wrapped in towels inside the carrier—never directly against the animal.',
                    'Monitor temps with a probe thermometer. Overheating in a small container in the sun is a faster killer than cold.'
                ]
            },
            {
                title: 'Carrier Setup',
                content: [
                    'Small is better for travel. A large carrier allows the animal to be tossed around during turns. Reptiles often prefer being in a tied fabric sack (pillowcase) inside a sturdy plastic tote box.',
                    'Birds need a perch installed in their carrier so they can grip, but nothing heavy that could swing and hit them.'
                ]
            }
        ]
    },
    {
        id: 'guide-care-12',
        title: 'Small Pet Housing: Cage Setup & Enrichment',
        description: 'Minimum space requirements for rabbits, guinea pigs, hamsters, and rats. Covers bedding selection, safe toys, exercise opportunities, cleaning schedules, and creating species-appropriate environments.',
        category: 'Small Pets',
        image: "https://img.rocket.new/generatedImages/rocket_gen_img_1434018d6-1767634235757.png",
        alt: 'Large multi-level habitat for small pets with enrichment items and proper bedding',
        readTime: '14 min read',
        difficulty: 'Beginner',
        rating: 4.8,
        reviewCount: 294,
        author: {
            name: 'Dr. Robert Williams',
            credentials: 'DVM, Small Mammal Husbandry',
            avatar: "https://img.rocket.new/generatedImages/rocket_gen_img_17e1c8542-1763293676786.png",
            avatarAlt: 'Professional portrait of Caucasian male veterinarian with beard in green scrubs'
        },
        tips: [
            "Avoid cedar and pine shavings (respiratory irritation & liver damage).",
            "Multiple levels increase space but verify ramps are safe.",
            "Spot clean 'wet spots' daily."
        ],
        sections: [
            {
                title: 'Space to Roam',
                content: [
                    'Small pets need more space than most commercial cages provide. Syrian hamsters need at least 450 sq inches of unbroken floor space. Rats need vertical height for climbing.',
                    'Aquariums are generally poor homes for rats/mice due to poor ventilation (ammonia buildup), but can work for hamsters if large enough.'
                ]
            },
            {
                title: 'The Art of Bedding',
                content: [
                    'Paper-based bedding (like Carefresh) or aspen shavings are safe choices. Avoid scented beddings which irritate sensitive respiratory tracts.',
                    'For burrowing animals like hamsters and gerbils, provide 6-8 inches of bedding depth so they can create tunnels. This is a primary form of enrichment for them.'
                ]
            }
        ]
    },
    // NEW GUIDES FOR SMALL MAMMALS & OTHER CATEGORIES
    {
        id: 'guide-care-13',
        title: 'Hamster Happiness: Enrichment & Care',
        description: 'Creating a stimulating environment for hamsters. Covers deep bedding for burrowing, wheel selection (size matters!), safe chews, and why generic "critter trails" are often unsuitable.',
        category: 'Small Pets',
        image: "https://images.unsplash.com/photo-1548767797-d8c844163c4c",
        alt: 'Cute syrup hamster peeking out of a coconut hide in a naturalistic enclosure',
        readTime: '10 min read',
        difficulty: 'Beginner',
        rating: 4.7,
        reviewCount: 156,
        author: {
            name: 'Dr. Robert Williams',
            credentials: 'DVM, Small Mammal Specialist',
            avatar: "https://img.rocket.new/generatedImages/rocket_gen_img_17e1c8542-1763293676786.png",
            avatarAlt: 'Professional portrait of Caucasian male veterinarian with beard in green scrubs'
        },
        tips: [
            "Wheel size: at least 8 inches for dwarfs, 11 inches for Syrians.",
            "Review bedding depth: aiming for 6+ inches for burrowing.",
            "Scatter feed instead of using a bowl to encourage foraging."
        ],
        sections: [
            {
                title: 'The Wheel Deal',
                content: [
                    'A wheel is not a toy; it is a necessity. In the wild, hamsters run up to 5 miles a night. However, small wheels cause back arching and pain.',
                    'Ensure the wheel has a solid surface (no mesh/wire to trap toes) and is large enough that the hamster\'s back remains completely flat while running.'
                ]
            },
            {
                title: 'Burrowing Instincts',
                content: [
                    'Hamsters are ground dwellers. They feel safest underground. Providing deep, paper-based bedding allows them to construct elaborate tunnel systems.',
                    'Multi-chamber hides mimic their natural burrows, offering separate "rooms" for sleeping, toileting, and food hoarding.'
                ]
            }
        ]
    },
    {
        id: 'guide-fish-1',
        title: 'Betta Fish Basics: Beyond the Bowl',
        description: 'Debunking the "puddle myth." Proper care for Betta splendens including heater requirements, filtration, tank size (5+ gallons), silk vs. plastic plants, and tank mate compatibility.',
        category: 'Fish & Aquatics',
        image: "https://images.unsplash.com/photo-1534575180408-f7e7daae35ca",
        alt: 'Vibrant blue and red halfmoon betta fish swimming in a planted tank',
        readTime: '12 min read',
        difficulty: 'Beginner',
        rating: 4.9,
        reviewCount: 412,
        author: {
            name: 'Dr. James Patterson',
            credentials: 'DVM, ABVP, Aquatic Medicine',
            avatar: "https://img.rocket.new/generatedImages/rocket_gen_img_1e95cb24a-1763296883553.png",
            avatarAlt: 'Professional photo of Caucasian male veterinarian with gray hair in blue medical uniform'
        },
        tips: [
            "Heater is mandatory: 78-80°F (25-27°C) is ideal.",
            "Never use a vase or bowl; 5 gallons is the minimum healthy size.",
            "Bettas can jump; always use a lid."
        ],
        sections: [
            {
                title: 'The Puddle Myth',
                content: [
                    'Bettas survive in shallow rice paddies during dry seasons, but they don\'t thrive there. These paddies are still part of vast water systems. Keeping a betta in a tiny bowl causes stress, immune suppression, and premature death from ammonia poisoning.',
                    'They require a filter like any other fish, but the flow should be gentle so it doesn\'t blow them around.'
                ]
            }
        ]
    },
    {
        id: 'guide-fish-2',
        title: 'Freshwater Aquarium Setup: Nitrogen Cycle Guide',
        description: 'The single most important concept for fish keeping. How to cycle a new tank BEFORE adding fish, understanding ammonia/nitrite/nitrate spikes, and establishing beneficial bacteria colonies.',
        category: 'Fish & Aquatics',
        image: "https://images.unsplash.com/photo-1520627725228-db9df8c99453",
        alt: 'Lush planted freshwater aquarium with neon tetras schooling',
        readTime: '20 min read',
        difficulty: 'Intermediate',
        rating: 5.0,
        reviewCount: 523,
        author: {
            name: 'Dr. James Patterson',
            credentials: 'DVM, ABVP, Aquatic Medicine',
            avatar: "https://img.rocket.new/generatedImages/rocket_gen_img_1e95cb24a-1763296883553.png",
            avatarAlt: 'Professional photo of Caucasian male veterinarian with gray hair in blue medical uniform'
        },
        tips: [
            "Buy a liquid test kit (API Master Kit), not strips.",
            "Cycling takes 4-6 weeks; be patient.",
            "Dechlorinate all tap water before adding it to the tank."
        ],
        sections: [
            {
                title: 'The Invisible Killer: Ammonia',
                content: [
                    'Fish waste produces ammonia, which is highly toxic. In a cycled tank, beneficial bacteria turn ammonia into nitrites (also toxic), and then into nitrates (less toxic).',
                    'If you add fish to a sterile new tank, ammonia builds up rapidly and burns their gills. This is "New Tank Syndrome." You must feed the bacteria (with fish food or pure ammonia) for weeks until the colony is large enough to handle a fish\'s bioload.'
                ]
            }
        ]
    }
];
